// react
import type { Dispatch, SetStateAction } from 'react';
// react-hook-form
import { Controller, useForm } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Autocomplete,
  Box,
  Chip,
  ListSubheader,
  Stack,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import type { GetEnergyTreeNodeDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
// shared-components
import { WritableOnly } from '@/components/access-control';
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFFieldLabel } from '@/components/ui/form';

// components
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

// relative
import InitiativeInclusionNotice from '../components/tree/InitiativeInclusionNotice';
import { validateMoveNode } from '../utils/energyTreeNodesValidationMiddleware';
import { toMoveNodeFormValues } from './moveNodeForm.mappers';
import type { MoveNodeFormValues } from './moveNodeForm.schema';
import { createMoveNodeFormSchema } from './moveNodeForm.schema';

// libraries
import { zodResolver } from '@hookform/resolvers/zod';

interface TreeNode extends GetEnergyTreeNodeDto {
  _path?: GetEnergyTreeNodeDto[];
  _hoverTimeout?: number;
}

type MoveNodeFormProps = {
  energyTreeNode?: GetEnergyTreeNodeDto;
  tree: GetEnergyTreeNodeDto[];
  treeOptions: TreeNode[];
  virtualInitiativeInclusions: Array<{
    initiative: { initiativeId: string; name?: string };
    viaNode: unknown;
  }>;
  hoveredPath: GetEnergyTreeNodeDto[];
  setHoveredPath: Dispatch<SetStateAction<GetEnergyTreeNodeDto[]>>;
  onSubmit: (values: MoveNodeFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
};

export default function MoveNodeForm({
  energyTreeNode,
  tree,
  treeOptions,
  virtualInitiativeInclusions,
  hoveredPath,
  setHoveredPath,
  onSubmit,
  onCancel,
  isSubmitting,
}: MoveNodeFormProps) {
  const { t } = useTranslation(['energyxplorer-tree-tab', 'validation']);
  const theme = useTheme();

  const schema = createMoveNodeFormSchema(t);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<MoveNodeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toMoveNodeFormValues(null),
    mode: 'onTouched',
  });

  const selectedParent =
    hoveredPath.length > 0 ? hoveredPath[hoveredPath.length - 1] : null;

  // Validation
  const validationResult = (() => {
    if (!energyTreeNode || !selectedParent)
      return { isValid: true, warnings: [] };

    return validateMoveNode({
      currentNode: energyTreeNode,
      parent: selectedParent,
      tree,
    });
  })();

  const handleFormSubmit = handleSubmit(onSubmit);

  if (!energyTreeNode) return null;

  return (
    <form onSubmit={handleFormSubmit} noValidate>
      <Stack spacing={2}>
        <Controller
          name="parentId"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <RHFFieldLabel required>
                {t('move_node_dialog.new_parent', {
                  ns: 'energyxplorer-tree-tab',
                })}
              </RHFFieldLabel>
              <Autocomplete<TreeNode>
                id="parent-node-selector"
                fullWidth
                size="small"
                options={treeOptions}
                getOptionLabel={(option) => option.name ?? ''}
                value={
                  treeOptions.find(
                    (opt) => opt.energyTreeNodeId === field.value,
                  ) ?? null
                }
                onChange={(event, newValue) => {
                  field.onChange(newValue?.energyTreeNodeId ?? null);
                }}
                groupBy={() => 'single-group'}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue.trim()) return options;

                  // Split input into words for flexible matching
                  const searchWords = inputValue
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(Boolean);

                  return options.filter((option) => {
                    const optionNameLower = option.name?.toLowerCase() || '';
                    const pathNames =
                      option._path?.map((n) => n.name?.toLowerCase() || '') ||
                      [];
                    const allNames = [optionNameLower, ...pathNames];

                    // Check if all search words appear in any of the names
                    return searchWords.every((word) =>
                      allNames.some((name) => name.includes(word)),
                    );
                  });
                }}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <ListSubheader
                      component="div"
                      sx={{ bgcolor: 'transparent' }}
                    >
                      <Chip
                        variant="filled"
                        size="large"
                        sx={{
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? '#121212'
                              : '#FFFFFF',
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                          color: theme.palette.text.primary,
                        }}
                        label={
                          <Box component="span">
                            {hoveredPath.map((n) => n.name).join(' / ')}
                            {energyTreeNode?.name && (
                              <Box
                                component="span"
                                sx={{
                                  color: validationResult.isValid
                                    ? theme.palette.success.main
                                    : theme.palette.error.main,
                                  fontWeight: 600,
                                  ml: 0.5,
                                }}
                              >
                                {validationResult.isValid ? '+ /' : '− /'}{' '}
                                {energyTreeNode.name}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListSubheader>

                    <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                  </li>
                )}
                renderOption={(props, option) => {
                  const { ...rest } = props;

                  return (
                    <li
                      {...rest}
                      key={option.energyTreeNodeId}
                      onMouseEnter={() => {
                        const path = option._path || [];
                        // Debounce hover updates to keep UI responsive
                        const timeout = window.setTimeout(
                          () => setHoveredPath(path),
                          150,
                        );
                        option._hoverTimeout = timeout;
                      }}
                      onMouseLeave={() => {
                        if (option._hoverTimeout) {
                          window.clearTimeout(option._hoverTimeout);
                          option._hoverTimeout = undefined;
                        }
                      }}
                      style={{
                        paddingLeft: `${(option._path?.length || 0) * 24}px`,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: getTypeColor(option?.type),
                          borderRadius: '25px',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginRight: 1,
                        }}
                      >
                        <i
                          style={{ color: 'white' }}
                          className={`fa-solid ${getTypeIcon(option?.type)}`}
                        />
                      </Box>
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    data-testid="move-node-parent-select"
                    placeholder={t('move_node_dialog.select_new_parent', {
                      item: energyTreeNode.name,
                      ns: 'energyxplorer-tree-tab',
                    })}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          )}
        />

        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="move"
        />
      </Stack>

      <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
        <CancelButton onClick={onCancel}>
          {t('move_node_dialog.cancel', {
            ns: 'energyxplorer-tree-tab',
          })}
        </CancelButton>
        <WritableOnly>
          <SubmitButton
            data-testid="move-node-confirm-button"
            loading={isSubmitting}
            disabled={!isValid}
          >
            {t('move_node_dialog.move', {
              ns: 'energyxplorer-tree-tab',
            })}
          </SubmitButton>
        </WritableOnly>
      </Box>
    </form>
  );
}
